import { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Modal, Form, InputNumber, Spin, message, Timeline } from 'antd';
import { vppDispatch } from '../../../services/api';

function formatTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('zh-CN', { hour12: false });
}

function parseResult(command) {
  if (!command.result) return {};
  try {
    return typeof command.result === 'string' ? JSON.parse(command.result) : command.result;
  } catch {
    return {};
  }
}

export default function VppDispatch() {
  const [loading, setLoading] = useState(true);
  const [commands, setCommands] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const loadData = async () => {
    try {
      const res = await vppDispatch.list();
      setCommands(res.data || []);
    } catch (err) {
      message.error(err.message || '加载调度指令失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleAccept = async (id) => {
    try {
      await vppDispatch.accept(id);
      message.success('已接收指令');
      loadData();
    } catch (err) {
      message.error(err.message || '接收失败');
    }
  };

  const handleExecute = async (id) => {
    try {
      await vppDispatch.execute(id);
      message.success('开始执行调度');
      loadData();
    } catch (err) {
      message.error(err.message || '执行失败');
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await vppDispatch.create({
        power_target: values.target_power,
        duration_minutes: values.duration,
        type: '计划调度',
      });
      message.success(`调度指令已发送: 目标功率${values.target_power}kW`);
      setModalOpen(false);
      form.resetFields();
      loadData();
    } catch (err) {
      if (err.message) message.error(err.message);
    }
  };

  const tableData = commands.map(cmd => {
    const result = parseResult(cmd);
    return {
      key: cmd.id,
      id: cmd.command_id || cmd.id,
      dbId: cmd.id,
      type: cmd.type,
      target: parseFloat(cmd.target_power),
      status: cmd.status,
      delay: cmd.response_delay_ms,
      accuracy: result.accuracy,
      start: formatTime(cmd.started_at || cmd.received_at || cmd.created_at),
      raw: cmd,
    };
  });

  const columns = [
    { title: '指令编号', dataIndex: 'id' },
    { title: '类型', dataIndex: 'type', render: t => <Tag>{t}</Tag> },
    { title: '目标功率(kW)', dataIndex: 'target', render: v => `${v} kW` },
    { title: '状态', dataIndex: 'status', render: v => {
      const colors = { '待接收': 'default', '已接收': 'blue', '执行中': 'orange', '已完成': 'green', '已取消': 'red' };
      return <Tag color={colors[v]}>{v}</Tag>;
    }},
    { title: '响应时延(ms)', dataIndex: 'delay', render: v => v || '-' },
    { title: '执行精度(%)', dataIndex: 'accuracy', render: v => v ? `${v}%` : '-' },
    { title: '开始时间', dataIndex: 'start' },
    { title: '操作', render: (_, record) => (
      <span>
        {record.status === '待接收' && <Button size="small" type="primary" style={{ marginRight: 8 }} onClick={() => handleAccept(record.dbId)}>接收</Button>}
        {record.status === '已接收' && <Button size="small" onClick={() => handleExecute(record.dbId)}>执行</Button>}
      </span>
    )},
  ];

  const executing = commands.filter(c => c.status === '执行中').length;
  const completed = commands.filter(c => c.status === '已完成');
  const avgDelay = completed.length
    ? Math.round(completed.reduce((s, c) => s + (c.response_delay_ms || 0), 0) / completed.length)
    : 0;
  const avgAccuracy = completed.length
    ? (completed.reduce((s, c) => s + (parseResult(c).accuracy || 0), 0) / completed.length).toFixed(1)
    : 0;

  const history = commands.slice(0, 5).map(cmd => ({
    time: formatTime(cmd.received_at || cmd.created_at),
    content: `[${cmd.status}] ${cmd.type} ${cmd.command_id || cmd.id}，目标功率 ${cmd.target_power}kW`,
  }));

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="调度指令总数" value={commands.length} suffix="条" /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="执行中" value={executing} suffix="条" valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="平均响应时延" value={avgDelay || '-'} suffix={avgDelay ? 'ms' : ''} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="平均执行精度" value={avgAccuracy || '-'} suffix={avgAccuracy ? '%' : ''} precision={1} valueStyle={{ color: '#1890ff' }} /></Card></Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={16}>
          <Card title="调度指令列表" size="small" extra={<Button type="primary" onClick={() => setModalOpen(true)}>新建调度指令</Button>}>
            <Table columns={columns} dataSource={tableData} pagination={false} size="small" locale={{ emptyText: '暂无指令，点击右上角新建' }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="调度执行记录" size="small">
            {history.length > 0
              ? <Timeline items={history.map(h => ({ children: <><div style={{ fontWeight: 'bold', fontSize: 12 }}>{h.time}</div><div>{h.content}</div></> }))} />
              : <div style={{ color: '#999' }}>暂无执行记录</div>}
          </Card>
        </Col>
      </Row>
      <Modal title="新建调度指令" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate}>
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="调度类型" initialValue="计划调度">
            <Tag>计划调度</Tag>
          </Form.Item>
          <Form.Item name="target_power" label="目标功率(kW)" rules={[{ required: true }]}>
            <InputNumber min={100} max={10000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="duration" label="持续时间(分钟)" initialValue={60}>
            <InputNumber min={15} max={480} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
